import React from 'react';
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { RootLayout } from './RootLayout';
import { PrivateRoutes } from './utils/PrivateRoutes';
import { PageNotFound } from './utils/PageNotFound';
import { RouteErrorBoundary } from './utils/RouteErrorBoundary';
import Home from './pages/Home';
import { Login } from './pages/Login';
import { Privacy } from './pages/Privacy';
import { CrewWrapper } from './pages/CrewWrapper';
import { AdminWrapper } from './pages/Admin/AdminWrapper';
import { PublicLayout } from './PublicLayout';
import { mapLegacyQuesoPath } from './utils/legacyQuesoRoutes';
import { ModuleGuard } from './components/ModuleGuard';

const BoardLabPage = React.lazy(() => import('./pages/BoardLabPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const HowToPage = React.lazy(() => import('./pages/HowTo'));
const HowToArticlePage = React.lazy(() => import('./pages/HowTo/HowToArticlePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const AdminHomePage = React.lazy(() =>
    import('./pages/Admin/AdminHomePage').then((module) => ({ default: module.AdminHomePage }))
);

const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const CrewHomePage = React.lazy(() => import('./pages/CrewHomePage'));
const MentorshipPage = React.lazy(() => import('./pages/MentorshipPage'));
const MentorshipProgramAdmin = React.lazy(() => import('./pages/MentorshipProgramAdmin'));
const CrewAdminsPage = React.lazy(() => import('./pages/CrewAdminsPage'));
const BulkCreateProfilesPage = React.lazy(() => import('./pages/BulkCreateProfilesPage'));
const CrewSettingsPage = React.lazy(() => import('./pages/CrewSettingsPage'));
const CrewInviteLinksPage = React.lazy(() => import('./pages/CrewInviteLinksPage'));
const JoinCrewPage = React.lazy(() => import('./pages/JoinCrewPage'));
const ProfileEditPage = React.lazy(() => import('./pages/ProfileEditPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ProfileMentorshipPreferencesPage = React.lazy(() => import('./pages/ProfileMentorshipPreferencesPage'));
const BadgeGalleryPage = React.lazy(() => import('./pages/BadgeGalleryPage'));
const BadgeManagementPage = React.lazy(() => import('./pages/BadgeManagementPage'));
const TeamManagementPage = React.lazy(() => import('./pages/TeamManagementPage'));
const TeamDetailPage = React.lazy(() => import('./pages/TeamDetailPage'));
const TeamEditPage = React.lazy(() => import('./pages/TeamEditPage'));
const TeamAssessmentPage = React.lazy(() => import('./pages/TeamAssessmentPage'));
const TeamCompetencyAnalysisPage = React.lazy(() => import('./pages/TeamCompetencyAnalysisPage'));
const ProfilesListPage = React.lazy(() => import('./pages/ProfilesListPage'));
const GlobalSearchPage = React.lazy(() => import('./pages/GlobalSearchPage'));
const AdminCrewCreationPage = React.lazy(() => import('./pages/Admin/AdminCrewCreationPage'));
const AdminCrewsPage = React.lazy(() => import('./pages/Admin/AdminCrewsPage'));
const SubscriptionManagementPage = React.lazy(() => import('./pages/Admin/AdminSubscriptionManagementPage'));
const AdminOrphanedResourcesPage = React.lazy(() => import('./pages/Admin/AdminOrphanedResourcesPage'));
const ExperimentsPage = React.lazy(() =>
    import('./pages/Admin/ExperimentsPage').then((m) => ({ default: m.ExperimentsPage }))
);
const CompetencyManagementPage = React.lazy(() => import('./pages/CompetencyManagementPage'));
const CompetencyDetailPage = React.lazy(() => import('./pages/CompetencyDetailPage'));
const CompetencyEditPage = React.lazy(() => import('./pages/CompetencyEditPage'));
const OrgStructureManagement = React.lazy(() => import('./pages/OrgStructurePage'));
const TeamCompetencyAssessmentPage = React.lazy(() => import('./pages/TeamCompetencyAssessmentPage'));
const ManagerDashboardPage = React.lazy(() => import('./pages/ManagerDashboardPage'));
const FormManagementPage = React.lazy(() =>
    import('./pages/FormManagementPage').then((module) => ({ default: module.FormManagementPage }))
);
const FormEditPage = React.lazy(() =>
    import('./pages/FormEditPage').then((module) => ({ default: module.FormEditPage }))
);
const FormViewPage = React.lazy(() =>
    import('./pages/FormViewPage').then((module) => ({ default: module.FormViewPage }))
);
const FormResponsesPage = React.lazy(() =>
    import('./pages/FormResponsesPage').then((module) => ({ default: module.FormResponsesPage }))
);
const TemplateManagementPage = React.lazy(() =>
    import('./pages/TemplateManagementPage').then((module) => ({ default: module.TemplateManagementPage }))
);
const TemplateEditPage = React.lazy(() =>
    import('./pages/TemplateEditPage').then((module) => ({ default: module.TemplateEditPage }))
);
const GenerateFormsPage = React.lazy(() =>
    import('./pages/GenerateFormsPage').then((module) => ({ default: module.GenerateFormsPage }))
);
const ArticlesListPage = React.lazy(() => import('./pages/ArticlesListPage'));
const BadgeDetailPage = React.lazy(() => import('./pages/BadgeDetailPage'));
const MyCompetenciesPage = React.lazy(() => import('./pages/MyCompetenciesPage'));
const MentorshipPeriodDetailsPage = React.lazy(() => import('./pages/MentorshipPeriodDetailsPage'));
const MentorshipPeriodEditPage = React.lazy(() => import('./pages/MentorshipPeriodEditPage'));
const ApiKeysPage = React.lazy(() => import('./pages/ApiKeysPage'));
const ApiDocsPage = React.lazy(() => import('./pages/ApiDocsPage'));
const GoalsPage = React.lazy(() => import('./pages/GoalsPage'));
const GoalDetailPage = React.lazy(() => import('./pages/GoalDetailPage'));
const PulsePage = React.lazy(() => import('./pages/PulsePage'));
const PulseCheckDetailPage = React.lazy(() => import('./pages/PulseCheckDetailPage'));
const PulseRespondPage = React.lazy(() => import('./pages/PulseRespondPage'));
const PulseInsightPage = React.lazy(() => import('./pages/PulseInsightPage'));
const QuickPollPage = React.lazy(() => import('./pages/QuickPollPage'));
const CrewSetupPage = React.lazy(() => import('./pages/CrewSetupPage'));
const BulkEditProfilesPage = React.lazy(() => import('./pages/BulkEditProfilesPage'));
const RetroBoardPage = React.lazy(() => import('./pages/RetroBoardPage'));
const RetroHistoryStandalonePage = React.lazy(() => import('./pages/RetroHistoryStandalonePage'));
const BoardPage = React.lazy(() => import('./pages/BoardPage'));
const MentorshipFeaturePage = React.lazy(() => import('./pages/features/MentorshipFeaturePage'));
const TeamsFeaturePage = React.lazy(() => import('./pages/features/TeamsFeaturePage'));
const CompetencyFeaturePage = React.lazy(() => import('./pages/features/CompetencyFeaturePage'));
const BadgesFeaturePage = React.lazy(() => import('./pages/features/BadgesFeaturePage'));
const TeamFeedPage = React.lazy(() => import('./pages/TeamFeedPage'));
const FeedPostPage = React.lazy(() => import('./pages/FeedPostPage'));
const FeedModQueuePage = React.lazy(() => import('./pages/FeedModQueuePage'));
const ProjectDetailPage = React.lazy(() => import('./components/project/ProjectDetailPage'));
const ProjectEditPage = React.lazy(() => import('./components/project/ProjectEditPage'));

const LegacyQuesoRedirect = () => {
    const { slug = '' } = useParams<{ slug: string }>();
    const location = useLocation();

    const redirectedPath = mapLegacyQuesoPath(location.pathname, slug);

    return <Navigate to={`${redirectedPath}${location.search}${location.hash}`} replace />;
};

export const router = createBrowserRouter([
    {
        element: <RootLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
            // Public routes
            { path: '/', element: <Home /> },
            { path: '/login', element: <Login /> },
            {
                element: <PublicLayout />,
                errorElement: <RouteErrorBoundary />,
                children: [
                    { path: '/privacy', element: <Privacy /> },
                    { path: '/blog', element: <BlogPage /> },
                    { path: '/blog/:slug', element: <BlogPostPage /> },
                    // { path: '/how-to', element: <HowToPage /> },
                    // { path: '/how-to/:slug', element: <HowToArticlePage /> },
                    { path: '/pricing', element: <PricingPage /> },
                    { path: '/features/mentorship', element: <MentorshipFeaturePage /> },
                    { path: '/features/teams', element: <TeamsFeaturePage /> },
                    { path: '/features/competencies', element: <CompetencyFeaturePage /> },
                    { path: '/features/badges', element: <BadgesFeaturePage /> }

                ]
            },

            // Private routes
            {
                element: <PrivateRoutes />,
                errorElement: <RouteErrorBoundary />,
                children: [
                    { path: '/onboarding', element: <OnboardingPage /> },
                    { path: '/join', element: <JoinCrewPage /> },
                    { path: '/board-lab', element: <BoardLabPage /> },

                    // Admin routes
                    {
                        path: '/admin',
                        element: <AdminWrapper />,
                        children: [
                            { path: '', element: <AdminHomePage /> },
                            { path: 'crews/create', element: <AdminCrewCreationPage /> },
                            { path: 'crews', element: <AdminCrewsPage /> },
                            { path: 'subscriptions', element: <SubscriptionManagementPage /> },
                            { path: 'orphaned-resources', element: <AdminOrphanedResourcesPage /> },
                            { path: 'experiments', element: <ExperimentsPage /> }
                        ]
                    },

                    // Crew routes
                    {
                        path: '/c/:slug',
                        element: <CrewWrapper />,
                        errorElement: <RouteErrorBoundary />,
                        children: [
                            { index: true, element: <CrewHomePage /> },
                            { path: 'setup', element: <CrewSetupPage /> },
                            // { path: 'articles', element: <ArticlesListPage /> },
                            { path: 'admins', element: <CrewAdminsPage /> },
                            { path: 'profiles/bulk-create', element: <BulkCreateProfilesPage /> },
                            { path: 'profiles/bulk-edit', element: <BulkEditProfilesPage /> },
                            { path: 'settings', element: <CrewSettingsPage /> },
                            { path: 'invite-links', element: <CrewInviteLinksPage /> },
                            { path: 'members', element: <ProfilesListPage /> },
                            { path: 'search', element: <GlobalSearchPage /> },
                            { path: 'profile/:handle', element: <ProfilePage /> },
                            { path: 'profile/:handle/edit', element: <ProfileEditPage /> },
                            { path: 'profile/:handle/mentorship', element: <ModuleGuard moduleId="mentorship"><ProfileMentorshipPreferencesPage /></ModuleGuard> },
                            { path: 'mentorship', element: <ModuleGuard moduleId={['mentorship', 'mentorshipPlans']}><MentorshipPage /></ModuleGuard> },
                            { path: 'mentorship/admin', element: <ModuleGuard moduleId={['mentorship', 'mentorshipPlans']}><MentorshipProgramAdmin /></ModuleGuard> },
                            { path: 'mentorship/plan/:periodId', element: <ModuleGuard moduleId="mentorshipPlans"><MentorshipPeriodDetailsPage /></ModuleGuard> },
                            { path: 'mentorship/plan/:periodId/edit', element: <ModuleGuard moduleId="mentorshipPlans"><MentorshipPeriodEditPage /></ModuleGuard> },
                            { path: 'badges', element: <ModuleGuard moduleId="badges"><BadgeGalleryPage /></ModuleGuard> },
                            { path: 'badges/manage', element: <ModuleGuard moduleId="badges"><BadgeManagementPage /></ModuleGuard> },
                            { path: 'badges/:badgeId', element: <ModuleGuard moduleId="badges"><BadgeDetailPage /></ModuleGuard> },
                            { path: 'competencies', element: <ModuleGuard moduleId="competencyDefinitions"><MyCompetenciesPage /></ModuleGuard> },
                            { path: 'competencies/assess', element: <ModuleGuard moduleId="orgChart"><TeamCompetencyAssessmentPage /></ModuleGuard> },
                            { path: 'competencies/dashboard', element: <ModuleGuard moduleId="orgChart"><ManagerDashboardPage /></ModuleGuard> },
                            { path: 'competencies/manage', element: <ModuleGuard moduleId="competencyDefinitions"><CompetencyManagementPage /></ModuleGuard> },
                            { path: 'competencies/manage/:competencyId', element: <ModuleGuard moduleId="competencyDefinitions"><CompetencyDetailPage /></ModuleGuard> },
                            { path: 'competencies/manage/:competencyId/edit', element: <ModuleGuard moduleId="competencyDefinitions"><CompetencyEditPage /></ModuleGuard> },
                            { path: 'org-structure', element: <ModuleGuard moduleId="orgChart"><OrgStructureManagement /></ModuleGuard> },
                            { path: 'teams', element: <TeamManagementPage /> },
                            { path: 'teams/:teamSlug', element: <TeamDetailPage /> },
                            { path: 'teams/:teamSlug/retros', element: <ModuleGuard moduleId="retrospectives"><RetroHistoryStandalonePage /></ModuleGuard> },
                            { path: 'teams/:teamSlug/retros/:retroId', element: <ModuleGuard moduleId="retrospectives"><RetroBoardPage /></ModuleGuard> },
                            { path: 'teams/:teamSlug/feed', element: <ModuleGuard moduleId="feeds"><TeamFeedPage /></ModuleGuard> },
                            { path: 'teams/:teamSlug/feed/:shortId', element: <ModuleGuard moduleId="feeds"><FeedPostPage /></ModuleGuard> },
                            { path: 'teams/:teamSlug/feed/reports', element: <ModuleGuard moduleId="feeds"><FeedModQueuePage /></ModuleGuard> },
                            { path: 'teams/:teamSlug/edit', element: <TeamEditPage /> },
                            { path: 'teams/:teamSlug/assess', element: <TeamAssessmentPage /> },
                            { path: 'teams/:teamSlug/competency-analysis', element: <TeamCompetencyAnalysisPage /> },
                            { path: 'forms', element: <FormManagementPage /> },
                            { path: 'forms/new', element: <FormEditPage /> },
                            { path: 'forms/:formId/edit', element: <FormEditPage /> },
                            { path: 'forms/:formId', element: <FormViewPage /> },
                            { path: 'forms/:formId/responses', element: <FormResponsesPage /> },
                            { path: 'workspace/forms', element: <TemplateManagementPage /> },
                            { path: 'workspace/forms/new', element: <TemplateEditPage /> },
                            { path: 'workspace/forms/edit/:templateId', element: <TemplateEditPage /> },
                            { path: 'workspace/forms/generate/:templateId', element: <GenerateFormsPage /> },
                            { path: 'api-keys', element: <ApiKeysPage /> },
                            { path: 'api-docs', element: <ApiDocsPage /> },
                            { path: 'goals', element: <ModuleGuard moduleId="goals"><GoalsPage /></ModuleGuard> },
                            { path: 'goals/:goalId', element: <ModuleGuard moduleId="goals"><GoalDetailPage /></ModuleGuard> },
                            { path: 'pulse', element: <ModuleGuard moduleId="pulseCheck"><PulsePage /></ModuleGuard> },
                            { path: 'poll/:pollId', element: <ModuleGuard moduleId="quickPoll"><QuickPollPage /></ModuleGuard> },
                            { path: 'pulse/:sessionId', element: <ModuleGuard moduleId="pulseCheck"><PulseCheckDetailPage /></ModuleGuard> },
                            { path: 'pulse/:sessionId/insight', element: <ModuleGuard moduleId="pulseCheck"><PulseInsightPage /></ModuleGuard> },
                            { path: 'pulse/respond/:token', element: <PulseRespondPage /> },
                            { path: 'boards/:boardId', element: <ModuleGuard moduleId="collabBoards"><BoardPage /></ModuleGuard> },
                            { path: 'projects/:shortId', element: <ModuleGuard moduleId="projects"><ProjectDetailPage /></ModuleGuard> },
                            { path: 'projects/:shortId/edit', element: <ModuleGuard moduleId="projects"><ProjectEditPage /></ModuleGuard> }
                        ]
                    }
                ]
            },

            // 404 catch-all
            { path: '*', element: <PageNotFound /> }
        ]
    }
]);
